/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import { PendingClinicRegistrationResponse } from "../../../models/clinic/PendingClinicRegistrationResponse";
import ClinicRegistrationApi from "../../../api/ClinicRegistrationApi";
import { getGeneralError } from "../../../util/helperFunctions";
import VerifyPhone from "../../../components/contact/user/phone/VerifyPhone";
import { PhoneConfirmationRequest } from "../../../models/contact/PhoneConfirmationRequest";
import AlertBox from "../../../components/AlertBox";

interface Props {
  registration?: PendingClinicRegistrationResponse;
  onNext(registration: PendingClinicRegistrationResponse): void;
}

const PhoneConfirmationTab: React.FC<Props> = (props: Props) => {
  const { registration, onNext } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [serverError, setServerError] = useState<null | string>(null);

  const submit = async (smsCode: string) => {
    if (!registration) return;
    setLoading(true);
    setServerError(null);

    try {
      const request: PhoneConfirmationRequest = { smsCode };
      const response = await ClinicRegistrationApi.confirmPhone(
        registration.id,
        request
      );
      onNext(response.data);
    } catch (e) {
      setServerError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (!registration) return;
    setLoading(true);
    setServerError(null);

    try {
      await ClinicRegistrationApi.sendPhoneConfirmation(registration.id);
    } catch (e) {
      setServerError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <VerifyPhone
        loading={isLoading}
        onSubmitCode={submit}
        phoneToConfirm={registration?.phones[0] || null}
        onResendCode={resendCode}
      />
      <AlertBox message={serverError} />
    </div>
  );
};

export default PhoneConfirmationTab;
